import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import { timeEntryService, customerService } from '../../services/api';
import { formatHoursMinutes } from '../../utils/formatHours';

// Employees read worked time as H:MM. The API already ships a formatted
// duration for serialized entries; the decimal `hours` field is the fallback.
const entryDuration = (entry) => entry.durationFormatted || formatHoursMinutes(entry.hours);

// Table styles (desktop only; a stacked card list replaces it at <=768px)
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 768px) {
    display: none;
  }
`;

// Mobile stacked card list (shown instead of the table at <=768px)
const MobileEntriesList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[3]};
    margin-top: ${({ theme }) => theme.spacing[4]};
  }
`;

const EntryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  background-color: ${({ theme }) => theme.colors.white};
`;

const EntryCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const EntryCardDate = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

const EntryCardMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const EntryCardHours = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const MobileEmpty = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.light};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightAlt};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
`;

// Status badge
const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: 9999px;

  ${({ status, theme }) => {
    if (status === 'approved') {
      return `
        background-color: var(--status-approved-plate);
        color: ${theme.colors.success};
      `;
    } else if (status === 'pending') {
      return `
        background-color: var(--status-warning-plate);
        color: ${theme.colors.warning};
      `;
    } else if (status === 'challenged') {
      return `
        background-color: rgba(195,37,29,.25);
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
`;

const ActionLink = styled(Link)`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    text-decoration: underline;
  }
`;

// Same link but with a >=44px tap target for the mobile card list
const CardActionLink = styled(ActionLink)`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[2]};
`;

// Search and filter
const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.dark};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-width: 150px;

  @media (max-width: 768px) {
    font-size: 16px; /* prevents iOS zoom on focus */
    min-height: 44px;
  }

  @media (max-width: 576px) {
    width: 100%;
    min-width: 0;
  }
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  @media (max-width: 768px) {
    font-size: 16px; /* prevents iOS zoom on focus */
    min-height: 44px;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const PageInfo = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.medium};
`;

const PageButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ErrorText = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: rgba(195,37,29,.25);
  color: ${({ theme }) => theme.colors.error};
`;

const TimeEntriesList = () => {
  const { t } = useTranslation();
  const [timeEntries, setTimeEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    customerId: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Load assigned customers for the filter dropdown
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const data = await customerService.getEmployeeCustomers();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching customers:', err);
      }
    };

    fetchCustomers();
  }, []);

  // Load time entries whenever the filters change
  useEffect(() => {
    let cancelled = false;

    const fetchTimeEntries = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = {};
        if (filter.customerId) params.customerId = filter.customerId;
        if (filter.status) params.status = filter.status;
        if (filter.dateFrom) params.startDate = filter.dateFrom;
        if (filter.dateTo) params.endDate = filter.dateTo;

        const entries = await timeEntryService.getEmployeeTimeEntries(params);
        if (!cancelled) {
          setTimeEntries(Array.isArray(entries) ? entries : []);
        }
      } catch (err) {
        console.error('Error fetching time entries:', err);
        if (!cancelled) {
          setError(t('employee.timeEntries.loadError'));
          setTimeEntries([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchTimeEntries();
    return () => { cancelled = true; };
  }, [filter]);

  // Paginate time entries
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = timeEntries.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(timeEntries.length / entriesPerPage);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  return (
    <Card
      title={t('employee.timeEntries.title')}
      subtitle={t('employee.timeEntries.subtitle')}
      padding="md"
      headerActions={
        <>
          <Button as={Link} to="/employee/calendar" variant="outline" style={{ marginRight: '8px' }}>{t('employee.timeEntries.calendarView')}</Button>
          <Button as={Link} to="/employee/time-entries/new">{t('employee.timeEntries.addNewEntry')}</Button>
        </>
      }
    >
      {/* Filters */}
      <FilterContainer>
        <FilterGroup>
          <FilterLabel htmlFor="customerId">{t('common.customer')}</FilterLabel>
          <FilterSelect
            id="customerId"
            name="customerId"
            value={filter.customerId}
            onChange={handleFilterChange}
          >
            <option value="">{t('employee.timeEntries.allCustomers')}</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="status">{t('common.status')}</FilterLabel>
          <FilterSelect
            id="status"
            name="status"
            value={filter.status}
            onChange={handleFilterChange}
          >
            <option value="">{t('employee.timeEntries.allStatuses')}</option>
            <option value="approved">{t('status.approved')}</option>
            <option value="pending">{t('status.pending')}</option>
            <option value="challenged">{t('status.challenged')}</option>
          </FilterSelect>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="dateFrom">{t('employee.timeEntries.from')}</FilterLabel>
          <FilterInput
            id="dateFrom"
            name="dateFrom"
            type="date"
            value={filter.dateFrom}
            onChange={handleFilterChange}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="dateTo">{t('employee.timeEntries.to')}</FilterLabel>
          <FilterInput
            id="dateTo"
            name="dateTo"
            type="date"
            value={filter.dateTo}
            onChange={handleFilterChange}
          />
        </FilterGroup>
      </FilterContainer>

      {/* Load error */}
      {error && <ErrorText>{error}</ErrorText>}

      {/* Time entries table */}
      <TableContainer>
        <Table>
          <TableHead>
            <tr>
              <TableHeadCell>{t('common.date')}</TableHeadCell>
              <TableHeadCell>{t('common.customer')}</TableHeadCell>
              <TableHeadCell>{t('common.project')}</TableHeadCell>
              <TableHeadCell>{t('common.hours')}</TableHeadCell>
              <TableHeadCell>{t('common.status')}</TableHeadCell>
              <TableHeadCell>{t('common.actions')}</TableHeadCell>
            </tr>
          </TableHead>
          <tbody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  {t('employee.timeEntries.loading')}
                </TableCell>
              </TableRow>
            ) : currentEntries.length > 0 ? (
              currentEntries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.date}</TableCell>
                  <TableCell>{entry.customerName || entry.customer?.name}</TableCell>
                  <TableCell>{entry.projectName || entry.project?.name || '—'}</TableCell>
                  <TableCell>{entryDuration(entry)}</TableCell>
                  <TableCell>
                    <Badge status={entry.status || 'pending'}>
                      {t(`status.${entry.status || 'pending'}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <ActionLink to={`/employee/time-entries/${entry.id}`}>
                      {t('employee.timeEntries.view')}
                    </ActionLink>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  {t('employee.timeEntries.noEntries')}
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </TableContainer>

      {/* Time entries as stacked cards (mobile only) */}
      <MobileEntriesList>
        {loading ? (
          <MobileEmpty>{t('employee.timeEntries.loading')}</MobileEmpty>
        ) : currentEntries.length > 0 ? (
          currentEntries.map((entry) => {
            const projectName = entry.projectName || entry.project?.name;
            return (
              <EntryCard key={entry.id}>
                <EntryCardRow>
                  <EntryCardDate>{entry.date}</EntryCardDate>
                  <Badge status={entry.status || 'pending'}>
                    {t(`status.${entry.status || 'pending'}`)}
                  </Badge>
                </EntryCardRow>
                <EntryCardMeta>
                  {entry.customerName || entry.customer?.name}
                  {projectName ? ` — ${projectName}` : ''}
                </EntryCardMeta>
                <EntryCardRow>
                  <EntryCardHours>
                    {t('common.hours')}: {entryDuration(entry)}
                  </EntryCardHours>
                  <CardActionLink to={`/employee/time-entries/${entry.id}`}>
                    {t('employee.timeEntries.view')}
                  </CardActionLink>
                </EntryCardRow>
              </EntryCard>
            );
          })
        ) : (
          <MobileEmpty>{t('employee.timeEntries.noEntries')}</MobileEmpty>
        )}
      </MobileEntriesList>

      {/* Pagination */}
      {!loading && timeEntries.length > 0 && (
        <Pagination>
          <PageInfo>
            {t('employee.timeEntries.showing', {
              from: indexOfFirstEntry + 1,
              to: Math.min(indexOfLastEntry, timeEntries.length),
              total: timeEntries.length,
            })}
          </PageInfo>
          <PageButtons>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              {t('common.previous')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              {t('common.next')}
            </Button>
          </PageButtons>
        </Pagination>
      )}
    </Card>
  );
};

export default TimeEntriesList;